import { useEffect, useState } from 'react'
import { useModal } from 'src/provider/ModalContextProvider'
import { useToast } from 'src/provider/ToastContextProvider'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { positionType } from 'src/pages/CouponMapPage'
import { Bridge } from 'src/utils/bridge'
import { useDelayTrackingMonitor } from 'src/utils/sentry/useDelayTrackingMonitor'

const useCurrentLocation = (when: boolean) => {
  const { openModal } = useModal()
  const { openToast } = useToast()
  const { channel } = useChannel()
  const [userLocation, setUserLocation] = useState<positionType | null>(null)

  // 현재 위치 요청 지연 추적
  const sentryMapDelayTracker = useDelayTrackingMonitor({
    feature: 'map_current_location',
    timeoutMs: 3_000,
  })

  const handleReject2TimesOverPermission = () => {
    const t = '위치정보가 필요합니다.'
    const d = '위치 권한을 허용하시면, 가까운 매장 및 쿠폰 혜택을 확인할 수 있습니다.'
    const button_submit_t = '권한 설정'
    const button_submit_f = () => Bridge.handleReqSystemLocation()
    const button_submit_cancel_t = '닫기'

    openModal({
      contents: { t, d },
      button: {
        submit: { t: button_submit_t, f: button_submit_f },
        cancel: { t: button_submit_cancel_t },
      },
    })
  }

  const handleCurrentLocation = {
    ios: () => Bridge.getLocationPermission.req(),
    aos: () => {
      if (Bridge.getLocationPermission.req()) {
        sentryMapDelayTracker.start()
        sentryMapDelayTracker.step('getLocationInfo_req')
        Bridge.getLocationInfo.req()
      } else {
        Bridge.getLocationPermissionShow.req()
          ? Bridge.handleReqLocationPermission()
          : handleReject2TimesOverPermission()
      }
    },
    web: async () => {
      if (navigator.geolocation) {
        try {
          const permissionStatus = await navigator.permissions.query({
            name: 'geolocation' as PermissionName,
          })

          if (permissionStatus.state === 'granted') {
            sentryMapDelayTracker.start()
            sentryMapDelayTracker.step('geolocation_req')
          }
        } catch {
          console.warn('permissions.query not supported')
        }

        navigator.geolocation.getCurrentPosition(
          (position: GeolocationPosition) => {
            sentryMapDelayTracker.step('geolocation_res')
            setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude })
          },
          (err: GeolocationPositionError) => {
            console.warn('Geolocation error: ', err)
            sentryMapDelayTracker.step('geolocation_error')
            sentryMapDelayTracker.clear()
            openToast({ contents: '브라우저 설정에서 위치권한을 허용해주세요.' })
          },
        )
      } else openToast({ contents: 'Geolocation을 지원하지 않는 브라우저입니다.' })
    },
  }

  useEffect(() => {
    Bridge.getLocationInfo.res(({ lat, lng }) => {
      sentryMapDelayTracker.step('getLocationInfo_res')
      setUserLocation({ lat: Number(lat), lng: Number(lng) })
    })
    Bridge.getLocationPermission.res()
    Bridge.getLocationPermissionShow.res(handleReject2TimesOverPermission)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (when) {
      handleCurrentLocation[channel]()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [when])

  return {
    userLocation,
    handleRetryCurLocation: () => handleCurrentLocation[channel](),
    sentryMapDelayTracker,
  }
}

export default useCurrentLocation
